import React, { useEffect, useState } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import AiTuneChangeTable, { isAiTuneAuditAction, isMarketSignalAuditAction, parseAiTuneAuditDetails } from './AiTuneChangeTable';

interface AuditRow {
  id: number;
  timestamp: string;
  action: string;
  entityType: string;
  entityId?: number | null;
  details?: string | null;
  level?: string | null;
  success?: boolean | null;
  errorMessage?: string | null;
  ipAddress?: string | null;
  adminUsername?: string | null;
}

type AuditFilterMode = 'all' | 'ai_tune' | 'market_signals';

interface IpContextMenuState {
  x: number;
  y: number;
  ipAddress: string;
}

const AuditLogs: React.FC = () => {
  const adminApi = useAdminApiService();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [filterMode, setFilterMode] = useState<AuditFilterMode>('all');
  const [entityIdFilter, setEntityIdFilter] = useState('');
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [ipMenu, setIpMenu] = useState<IpContextMenuState | null>(null);
  const [blockingIp, setBlockingIp] = useState(false);

  const loadAuditLogs = async (pageIndex: number, mode: AuditFilterMode = filterMode, entityId: string = entityIdFilter) => {
    setLoading(true);
    setError(null);
    try {
      const filters: {
        actionPrefix?: string;
        entityType?: string;
        entityId?: number;
        aiTuneOnly?: boolean;
        marketSignalOnly?: boolean;
      } = {};

      if (mode === 'ai_tune') {
        filters.aiTuneOnly = true;
        filters.entityType = 'TRADING_STRATEGY';
      }

      if (mode === 'market_signals') {
        filters.marketSignalOnly = true;
        filters.entityType = 'MARKET_SIGNAL';
      }

      const parsedEntityId = entityId.trim() ? Number(entityId.trim()) : undefined;
      if (parsedEntityId != null && !Number.isNaN(parsedEntityId)) {
        filters.entityId = parsedEntityId;
        if (mode === 'all') {
          filters.entityType = 'TRADING_STRATEGY';
        }
      }

      const res = await adminApi.getAuditLogs(pageIndex, size, filters);
      if (res.success && res.data) {
        const payload: any = res.data;
        setRows(Array.isArray(payload.content) ? payload.content : []);
        setPage(Number(payload.page) || 0);
        setTotalPages(Number(payload.totalPages) || 0);
      } else {
        setError(res.error || 'Failed to load audit logs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs(0);
  }, []);

  useEffect(() => {
    if (!ipMenu) return;
    const close = () => setIpMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ipMenu]);

  const applyFilters = () => {
    setExpandedRowId(null);
    loadAuditLogs(0, filterMode, entityIdFilter);
  };

  const openIpMenu = (event: React.MouseEvent, ipAddress?: string | null) => {
    if (!ipAddress || !ipAddress.trim()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setIpMenu({
      x: event.clientX,
      y: event.clientY,
      ipAddress: ipAddress.trim(),
    });
  };

  const addIpToBlocklist = async () => {
    if (!ipMenu) return;
    const ip = ipMenu.ipAddress;
    setIpMenu(null);
    setBlockingIp(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminApi.addIpToBlocklist(ip, 'Flagged from Audit Logs');
      if (res.success) {
        setSuccess(`IP ${ip} added to blocklist. Open Blocklist to review or disable.`);
      } else {
        setError(res.error || `Failed to block IP ${ip}`);
      }
    } catch (err: any) {
      setError(err.message || `Failed to block IP ${ip}`);
    } finally {
      setBlockingIp(false);
    }
  };

  const renderDetailsCell = (row: AuditRow) => {
    if (row.errorMessage) {
      return row.errorMessage;
    }

    if (isAiTuneAuditAction(row.action) || isMarketSignalAuditAction(row.action)) {
      const parsed = parseAiTuneAuditDetails(row.details);
      const hasDiff = parsed.changes.length > 0;
      const isExpanded = expandedRowId === row.id;

      return (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{parsed.summary}</span>
            {hasDiff && (
              <button
                type="button"
                onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                style={{
                  border: '1px solid #c4b5fd',
                  background: '#faf5ff',
                  color: '#6d28d9',
                  borderRadius: 999,
                  padding: '2px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {isExpanded ? 'Hide diff' : `Show diff (${parsed.changes.length})`}
              </button>
            )}
          </div>
          {isExpanded && hasDiff && (
            <AiTuneChangeTable changes={parsed.changes} compact />
          )}
        </div>
      );
    }

    return row.details || '-';
  };

  return (
    <div className="admin-component">
      <div className="section-header">
        <h2 className="section-title">Audit Logs</h2>
        <button className="refresh-btn" onClick={() => loadAuditLogs(page)} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12, alignItems: 'center' }}>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
          Filter:
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value as AuditFilterMode)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          >
            <option value="all">All actions</option>
            <option value="ai_tune">AI Auto-Tune only</option>
            <option value="market_signals">Market signals only</option>
          </select>
        </label>
        {filterMode !== 'market_signals' && (
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
          Strategy ID:
          <input
            type="text"
            value={entityIdFilter}
            onChange={(e) => setEntityIdFilter(e.target.value)}
            placeholder="e.g. 281"
            style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />
        </label>
        )}
        <button className="refresh-btn" onClick={applyFilters} disabled={loading}>
          Apply filters
        </button>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          Right-click a Client IP to add it to the blocklist
        </span>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {success && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            marginBottom: 12,
            background: '#ecfdf5',
            border: '1px solid #6ee7b7',
            borderRadius: 8,
            color: '#047857',
            fontSize: 13,
          }}
        >
          <span>{success}</span>
          <button
            type="button"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16 }}
          >
            ×
          </button>
        </div>
      )}

      {!error && (
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 10 }}>Time</th>
                <th style={{ textAlign: 'left', padding: 10 }}>User</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Client IP</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Action</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Entity</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Status</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} style={{ padding: 16, color: '#6b7280' }}>No audit logs found.</td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ padding: 10 }}>{row.timestamp ? new Date(row.timestamp).toLocaleString() : 'N/A'}</td>
                  <td style={{ padding: 10 }}>{row.adminUsername || 'SYSTEM'}</td>
                  <td
                    style={{
                      padding: 10,
                      fontFamily: 'monospace',
                      fontSize: 12,
                      cursor: row.ipAddress ? 'context-menu' : 'default',
                      userSelect: 'none',
                      color: row.ipAddress ? '#111827' : '#9ca3af',
                    }}
                    title={row.ipAddress ? 'Right-click to add to blocklist' : undefined}
                    onContextMenu={(e) => openIpMenu(e, row.ipAddress)}
                  >
                    {row.ipAddress || '—'}
                  </td>
                  <td style={{ padding: 10, fontWeight: 600 }}>{row.action}</td>
                  <td style={{ padding: 10 }}>{row.entityType}{row.entityId ? ` #${row.entityId}` : ''}</td>
                  <td style={{ padding: 10 }}>
                    <span style={{ color: row.success ? '#15803d' : '#b91c1c', fontWeight: 700 }}>
                      {row.success ? 'SUCCESS' : 'FAILED'}
                    </span>
                  </td>
                  <td style={{ padding: 10, maxWidth: 640, whiteSpace: 'normal' }}>
                    {renderDetailsCell(row)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button
          className="refresh-btn"
          onClick={() => loadAuditLogs(Math.max(0, page - 1))}
          disabled={loading || page <= 0}
        >
          Prev
        </button>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          Page {page + 1} / {Math.max(totalPages, 1)}
        </span>
        <button
          className="refresh-btn"
          onClick={() => loadAuditLogs(page + 1)}
          disabled={loading || page + 1 >= totalPages}
        >
          Next
        </button>
      </div>

      {ipMenu && (
        <div
          role="menu"
          style={{
            position: 'fixed',
            top: ipMenu.y,
            left: ipMenu.x,
            zIndex: 10000,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 200,
            padding: 4,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            disabled={blockingIp}
            onClick={addIpToBlocklist}
            style={{
              width: '100%',
              textAlign: 'left',
              border: 'none',
              background: 'transparent',
              padding: '10px 12px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'Nunito, sans-serif',
              color: '#b91c1c',
              fontWeight: 700,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fef2f2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {blockingIp ? 'Blocking…' : `Add ${ipMenu.ipAddress} to Blocklist`}
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
